/**
 * Simplified bill/invoice matching from Xero tracking categories.
 * Job: the slot set to Name, Number, or Both → fallback "Job Name".
 * A second slot (for example Division) is ignored.
 * PO: "Jobtread PO Number" only.
 */

import { jobIdentitySlot } from './trackingSlots.js';

export const JOB_NAME_CATEGORY = 'Job Name';
export const JOBTREAD_PO_CATEGORY = 'Jobtread PO Number';

/**
 * Category name that holds the job identity.
 * A string is the older "just the category name" call.
 * A settings object uses the Name / Number / Both slot.
 */
function identityCategoryName(settingsOrName, fieldKey) {
  if (settingsOrName == null || settingsOrName === '') return null;
  if (typeof settingsOrName === 'string') return settingsOrName;
  const slot = jobIdentitySlot(settingsOrName, fieldKey);
  return slot?.xeroCategoryName || null;
}

/**
 * Get job value from line items. Primary: the job-identity category; fallback: "Job Name".
 * @param {Array} lineItems - Bill/invoice line items with tracking
 * @param {string|object|null} settingsOrName - Category name, or bill/invoice tracking settings
 * @param {string} [fieldKey] - 'jtBillNameField' or 'jtJobField' when settings are passed
 * @returns {string|null}
 */
export function getJobValueFromLineItems(lineItems, settingsOrName, fieldKey = 'jtBillNameField') {
  if (!lineItems?.length) return null;
  const categoryName = identityCategoryName(settingsOrName, fieldKey);
  if (categoryName) {
    for (const item of lineItems) {
      const primary = (item.tracking || []).find(t => t.name === categoryName && t.option);
      if (primary) return primary.option;
    }
  }
  for (const item of lineItems) {
    const fallback = (item.tracking || []).find(t => t.name === JOB_NAME_CATEGORY && t.option);
    if (fallback) return fallback.option;
  }
  return null;
}

/**
 * True when a tracking option is this job's name, number, or "Name-Number".
 */
export function jobMatchesTrackingValue(job, value) {
  if (!job || value == null) return false;
  const search = String(value).trim().toLowerCase();
  if (!search) return false;
  const name = String(job.name || '').trim().toLowerCase();
  const number = job.number != null ? String(job.number).trim().toLowerCase() : '';
  if (name && (name === search || name.includes(search) || search.includes(name))) return true;
  if (number && number === search) return true;
  if (name && number && (search === `${name}-${number}` || search.endsWith(`-${number}`))) return true;
  return false;
}

/**
 * Get Jobtread PO value from line items. Uses "Jobtread PO Number" only.
 * @param {Array} lineItems
 * @returns {string|null}
 */
export function getPOValueFromLineItems(lineItems) {
  if (!lineItems?.length) return null;
  for (const item of lineItems) {
    const jtPO = (item.tracking || []).find(t => t.name === JOBTREAD_PO_CATEGORY && t.option);
    if (jtPO) return jtPO.option;
  }
  return null;
}

/**
 * Get all unique PO values from line items (for multiple-PO scenarios).
 * @param {Array} lineItems
 * @returns {string[]}
 */
export function getAllPOValuesFromLineItems(lineItems) {
  if (!lineItems?.length) return [];
  const seen = new Set();
  const result = [];
  for (const item of lineItems) {
    const tracking = item.tracking || [];
    for (const t of tracking) {
      if (t.name === JOBTREAD_PO_CATEGORY && t.option && !seen.has(t.option)) {
        seen.add(t.option);
        result.push(t.option);
      }
    }
  }
  return result;
}

/**
 * Normalize a PO number for comparison by stripping leading zeros.
 * @param {*} val
 * @returns {string}
 */
export function normalizePO(val) {
  if (val == null) return '';
  return String(val).replace(/^0+/, '') || '0';
}
